import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, LogIn } from 'lucide-react';

// Detect the correct API base URL at RUNTIME
function getApiBase() {
  // Try relative first (works when proxy or ingress handles routing)
  return '/api';
}

// Fallback: try origin-based URL if relative fails
function getApiFallback() {
  return window.location.origin + '/api';
}

export default function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiBase, setApiBase] = useState(getApiBase());

  // On mount, verify API connectivity
  useEffect(() => {
    async function checkApi() {
      try {
        const res = await fetch('/api/health', { signal: AbortSignal.timeout(5000) });
        if (res.ok) {
          setApiBase('/api');
          return;
        }
      } catch {}
      // Try with origin
      try {
        const res2 = await fetch(window.location.origin + '/api/health', { signal: AbortSignal.timeout(5000) });
        if (res2.ok) {
          setApiBase(window.location.origin + '/api');
          return;
        }
      } catch {}
    }
    checkApi();
  }, []);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${apiBase}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Email ou senha incorretos');
      }

      const data = await response.json();
      localStorage.setItem('session_token', data.session_token);
      navigate('/dashboard', { state: { user: data.user } });
    } catch (err) {
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setError('Servidor indisponivel. Tente novamente em alguns segundos.');
      } else {
        setError(err.message || 'Erro ao fazer login');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src="https://customer-assets.emergentagent.com/job_esports-league-5/artifacts/0z14gmwv_logo.png" 
              alt="SEXTOUCUP" 
              className="h-16 w-16 mx-auto mb-4"
            />
            <h1 className="text-3xl font-unbounded font-bold text-white">SEXTOUCUP</h1>
          </Link>
        </div>

        <div className="glass-card p-8 rounded-2xl">
          <h2 className="text-2xl font-unbounded font-semibold text-white mb-6 text-center">
            Entrar
          </h2>

          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            onClick={handleGoogleLogin}
            className="w-full bg-white text-obsidian font-semibold px-6 py-3 rounded-lg hover:bg-zinc-100 transition-colors mb-6 flex items-center justify-center gap-2"
            data-testid="google-login-button"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar com Google
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-obsidian-card text-zinc-500">ou</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-mono uppercase tracking-widest text-gold-500/80 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder:text-zinc-600 focus:border-gold-400/50 focus:ring-1 focus:ring-gold-400/50 rounded-lg h-12 pl-10 pr-4 transition-all"
                  placeholder="seu@email.com"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-mono uppercase tracking-widest text-gold-500/80 mb-2">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder:text-zinc-600 focus:border-gold-400/50 focus:ring-1 focus:ring-gold-400/50 rounded-lg h-12 pl-10 pr-4 transition-all"
                  placeholder="********"
                  data-testid="password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gold-400 text-obsidian font-bold px-6 py-3 rounded-lg hover:bg-gold-300 transition-all duration-300 uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="login-submit-button"
            >
              {loading ? 'Entrando...' : (
                <>
                  <LogIn className="h-5 w-5" />
                  Entrar
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-400">
              Nao tem uma conta?{' '}
              <Link to="/register" className="text-gold-400 hover:text-gold-300 transition-colors font-semibold" data-testid="register-link">
                Cadastre-se
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-zinc-500 hover:text-zinc-400 transition-colors" data-testid="back-home-link">
            Voltar para o inicio
          </Link>
        </div>
      </div>
    </div>
  );
}
